import Papa from 'papaparse';

/**
 * Converts an array of objects to CSV format with automatic array flattening.
 *
 * @description This function flattens nested array properties and converts the data to CSV
 * using Papa Parse. Arrays are joined with a configurable delimiter and properly quoted
 * to handle commas and special characters.
 *
 * SUPPORTED DATA TYPES:
 * - Primitive types: string, number, boolean
 * - Array types: string[], number[]
 * - Mixed arrays: (string | number)[]
 *
 * UNSUPPORTED DATA TYPES:
 * - Objects, nested objects
 * - Arrays containing other types: boolean[], object[], Date[], etc.
 * - Functions, undefined, null, Symbol
 *
 * @param tabularData - Array of objects containing only supported data types
 * @param delimiter - Delimiter used to join array elements (default: ', ')
 * @returns CSV string with headers and properly escaped values
 *
 * @throws Error when encountering unsupported data types
 *
 * @example
 * // Basic usage
 * const data = [
 *   { name: 'John', age: 30, active: true },
 *   { name: 'Jane', age: 25, active: false }
 * ];
 * const csv = tabularDataToCsv(data);
 *
 * @example
 * // With arrays
 * const data = [
 *   { name: 'Campaign A', tags: ['marketing', 'display'], scores: [85, 92] },
 *   { name: 'Campaign B', tags: ['social', 'video'], scores: [78, 89] }
 * ];
 * const csv = tabularDataToCsv(data, '; '); // Custom delimiter
 */
function tabularDataToCsv<DataType extends object>(
  tabularData: DataType[],
  {
    delimiter = ', ',
  }: {
    delimiter?: string;
  } = {
    delimiter: ', ',
  },
): string {
  const flattenedData = tabularData.map((item) => {
    const flattened: Record<string, string | number | boolean> = {};

    for (const [key, value] of Object.entries(item)) {
      if (Array.isArray(value)) {
        // Handle string and number arrays
        if (value.every((item) => typeof item === 'string')) {
          // Wrap string items in quotes to handle commas within strings
          // Escape any existing quotes by doubling them
          flattened[key] = value.map((item) => `"${item.replace(/"/g, '""')}"`).join(delimiter);
        } else if (value.every((item) => typeof item === 'number')) {
          // Numbers don't need quotes
          flattened[key] = value.join(delimiter);
        } else if (value.every((item) => typeof item === 'string' || typeof item === 'number')) {
          // Mixed arrays: wrap strings in quotes, leave numbers as-is
          flattened[key] = value
            .map((item) =>
              typeof item === 'string' ? `"${item.replace(/"/g, '""')}"` : String(item),
            )
            .join(delimiter);
        } else {
          throw new Error(
            `Array property '${key}' contains unsupported values. Only string[] and number[] arrays are supported.`,
          );
        }
      } else if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        flattened[key] = value;
      } else {
        throw new Error(
          `Property '${key}' has unsupported type '${typeof value}'. Only string, number, boolean, string[], or number[] are allowed.`,
        );
      }
    }

    return flattened;
  });

  const csv = Papa.unparse(flattenedData, {
    quotes: true,
    quoteChar: '"',
    delimiter: ',',
    header: true,
    skipEmptyLines: true,
    newline: '\n',
  });

  return '```csv\n' + csv + '\n```';
}

export default tabularDataToCsv;
